import { motion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { ArrowRight, ArrowDown, Zap, Shield, Heart, Award, Activity, Phone, Star } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import PaymentOptions from '../components/PaymentOptions';
import { CLINIC } from '../data/clinic';
import { LOCAL_BUSINESS_LD, RATING } from '../data/seo';
import { pathFor, localize } from '../data/routes';
import { useLang } from '../i18n';

const HERO_IMG = '/img/hero.jpg';
const DRX_HERO = '/equipment/drx-device.png';
const DR_PHOTO = '/img/dr-ruiz.jpg';
const PI_IMG = 'https://images.unsplash.com/photo-1758525589764-f27eb35400c9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900';

const SVC_IMAGES = [
  'https://customer-assets.emergentagent.com/job_elite-spine-preview/artifacts/uok78t01_Expert%20Sports%20Injury%20Treatment%20in%20Belleville%2C%20NJ.jpg',
  'https://customer-assets.emergentagent.com/job_elite-spine-preview/artifacts/aryyn9ki_Chiropractic%20Adjustments%20in%20Santa%20Barbara%2C%20CA.jpg',
  'https://customer-assets.emergentagent.com/job_elite-spine-preview/artifacts/hy7jsqj9_Expert%20Evaluation%20for%20Pediatric%20Spine%20Conditions.jpg',
  'https://customer-assets.emergentagent.com/job_elite-spine-preview/artifacts/m9df7arn_Sports%20Injury%20Treatment.jpg',
];
const SVC_META = [
  { icon: Zap, color: '#1E4E8C', link: '/walk-in' },
  { icon: Shield, color: '#1B1815', link: '/services' },
  { icon: Heart, color: '#1E4E8C', link: '/services' },
  { icon: Award, color: '#17407A', link: '/services' },
];
const SPEC_ICONS = [Shield, Heart, Award, Activity];
const TECH_IMAGES = [
  '/equipment/drx9000-combo.png',
  '/equipment/piezowave.png',
  '/equipment/arthrostim.png',
  '/equipment/denneroll.png',
  '/equipment/prolordotic.png',
];

const ease = [0.22, 1, 0.36, 1];
const CLIP = 'polygon(0 0, 100% 0, 100% 93%, 93% 100%, 0 100%)';

const HomePage = () => {
  const { t, lang } = useLang();
  const { scrollYProgress } = useScroll();
  const heroImgY = useTransform(scrollYProgress, [0, 0.4], [0, -120]);
  const heroImgScale = useTransform(scrollYProgress, [0, 0.3], [1, 1.1]);
  const textY = useTransform(scrollYProgress, [0, 0.3], [0, -60]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const tiltX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 16 });
  const tiltY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 16 });
  const onHeroMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const heroLines = [
    <>{t.hero.l1} <span className="italic text-[#17407A]">{t.hero.l2a}</span></>,
    <>{t.hero.l2b} <span className="italic text-[#1E4E8C]">{t.hero.l3}</span></>,
    <>{t.hero.l4}</>,
  ];
  const marquee = [...t.marquee, ...t.marquee];

  return (
    <div data-testid="home-page">
      <SEOHead
        title={t.seo.home.title}
        description={t.seo.home.description}
        lang={lang}
        enPath={pathFor('home', 'en')}
        esPath={pathFor('home', 'es')}
        jsonLd={LOCAL_BUSINESS_LD}
      />

      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex items-center overflow-hidden" style={{ background: '#F2F0ED' }} data-testid="hero-section" onMouseMove={onHeroMouse}>
        <motion.div style={{ y: heroImgY, scale: heroImgScale }} className="absolute right-[-10%] top-[-8%] h-[120%] w-[55%] z-0 pointer-events-none">
          <motion.img
            src={HERO_IMG} alt=""
            className="h-full w-full object-cover object-top animate-hero-float mix-blend-multiply"
            style={{ filter: 'grayscale(12%) brightness(1.02) contrast(0.98)' }}
            initial={{ opacity: 0, x: 100, scale: 1.05 }}
            animate={{ opacity: 0.35, x: 0, scale: 1 }}
            transition={{ duration: 2.6, ease }}
          />
        </motion.div>
        <div className="absolute inset-0 hero-gradient z-[1]" />
        <div className="absolute bottom-0 left-0 right-0 h-48 hero-gradient-bottom z-[1]" />

        <motion.div style={{ y: textY, opacity: heroOpacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-28 w-full">
          <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }} className="inline-block text-[#1E4E8C] text-[13px] tracking-[0.3em] uppercase mb-8 font-medium">
            {t.hero.eyebrow}
          </motion.span>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl xl:text-8xl leading-[1.12] max-w-4xl">
            {heroLines.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span className="block" initial={{ y: '115%' }} animate={{ y: 0 }} transition={{ duration: 1.05, delay: 0.25 + i * 0.13, ease }}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="text-[19px] sm:text-[22px] text-[#3A342E] mt-9 mb-11 max-w-xl leading-[1.6] font-medium">
            {t.hero.body}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }} className="flex flex-col sm:flex-row flex-wrap gap-4">
            <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href={CLINIC.bookingUrl} target="_blank" rel="noreferrer" className="btn-primary" data-testid="hero-book-btn">{t.common.book} <ArrowRight className="w-4 h-4" /></motion.a>
            <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href={CLINIC.phoneHref} className="btn-outline" data-testid="hero-call-btn" style={{ borderColor: '#1B1815', color: '#1B1815' }}><Phone className="w-4 h-4" /> {t.common.call} — {CLINIC.phone}</motion.a>
            <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href={localize('/walk-in', lang)} className="btn-outline" data-testid="hero-walkin-btn" style={{ borderColor: '#1E4E8C', color: '#1E4E8C' }}>⚡ {t.common.walkin}</motion.a>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.7 }} className="mt-8 flex items-center gap-3" data-testid="hero-rating">
            <span className="flex items-center gap-0.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-[18px] h-[18px]" style={{ color: '#E0A63C', fill: '#E0A63C' }} />
              ))}
            </span>
            <span className="font-body text-[15px] text-[#3A342E]">
              <strong className="text-[#1B1815]">{RATING.value}</strong> · {RATING.count} Google reviews
            </span>
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
          <span className="text-[#A79E95] text-[11px] tracking-[0.4em] uppercase">{t.common.scroll}</span>
          <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}>
            <ArrowDown className="w-3.5 h-3.5 text-[#17407A]" />
          </motion.div>
        </motion.div>
      </section>

      {/* ===== URGENT AFTER-HOURS STRIP ===== */}
      <section className="bg-[#1E4E8C]" data-testid="home-after-hours">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-7 lg:py-8">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4 md:items-center">
              <span className="relative mt-1 flex h-3 w-3 shrink-0 md:mt-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E8C87E] opacity-70" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-[#E8C87E]" />
              </span>
              <p className="font-body text-[16px] leading-relaxed text-[#FBF8F2] md:text-[17px]">
                <span className="font-display font-semibold" style={{ color: '#E8C87E' }}>{t.schedule.afterHoursTitle} </span>
                {t.schedule.afterHoursBody}
              </p>
            </div>
            <a
              href={CLINIC.phoneHref}
              data-testid="home-after-hours-call-btn"
              className="inline-flex shrink-0 items-center justify-center gap-3 whitespace-nowrap bg-[#B4882F] px-8 py-4 font-body text-[14px] font-semibold uppercase tracking-[0.12em] text-[#1B1815] transition-colors hover:bg-[#C89A3E]"
            >
              <Phone className="h-4 w-4" /> {t.schedule.afterHoursBtn}
            </a>
          </div>
        </div>
      </section>

      {/* ===== MARQUEE ===== */}
      <div className="py-6 bg-[#1B1815] overflow-hidden" data-testid="marquee">
        <div className="marquee-track">
          {marquee.map((word, i) => (
            <span key={i} className="font-serif italic text-[#C7D6EA] text-2xl sm:text-3xl px-8 flex items-center gap-8">
              {word} <span className="text-[#1E4E8C] not-italic">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* ===== NEW-PATIENT OFFER BAND ===== */}
      <section className="py-14 lg:py-16 bg-[#1B1815]" data-testid="offer-band">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease }} className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left max-w-2xl">
              <span className="text-white/70 text-[13px] tracking-[0.3em] uppercase font-medium">{t.offer.eyebrow}</span>
              <h2 className="font-serif text-3xl sm:text-4xl text-white mt-2">{t.offer.title}</h2>
              <p className="text-white/85 text-[18px] mt-3 leading-relaxed">{t.offer.desc}</p>
              <p className="text-white/60 text-[14px] mt-3">{t.offer.note}</p>
            </div>
            <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href={CLINIC.bookingUrl} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-2 bg-white text-[#1E4E8C] px-9 py-5 text-[15px] tracking-[0.12em] uppercase font-semibold hover:bg-[#F2F0ED] transition-colors" data-testid="offer-band-btn">
              {t.offer.cta} <ArrowRight className="w-4 h-4" />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* ===== ABOUT DR. RUIZ ===== */}
      <section className="py-24 lg:py-32 bg-[#FFFFFF]" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-20 items-start">
            <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease }} className="space-y-6 min-w-0">
              <div className="aspect-[4/5] overflow-hidden" style={{ clipPath: CLIP }}>
                <img src={DR_PHOTO} alt="Dr. Matthew Ruiz" className="w-full h-full object-cover object-top" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                {t.stats.map((stat, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.6 }} className="text-center py-5 border-t-2 border-[#1B1815]/15">
                    <span className="font-serif text-3xl sm:text-4xl text-[#17407A]">{stat.num}</span>
                    <p className="text-[#A79E95] mt-1 text-[11px] sm:text-[12px] tracking-[0.12em] uppercase leading-tight break-words px-1">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1, duration: 0.8 }} className="min-w-0">
              <span className="text-[#1E4E8C] text-[13px] tracking-[0.3em] uppercase font-medium">{t.about.eyebrow}</span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1B1815] mt-4 mb-6">{t.about.name}</h2>
              <div className="space-y-4 text-[#3A342E] leading-relaxed text-[17px]">
                <p>{t.about.p1}</p>
                <p>{t.about.p2}</p>
                <p>{t.about.p3}</p>
              </div>
              <div className="my-8 pl-6 border-l-2 border-[#1E4E8C]/30">
                <blockquote className="font-serif text-xl text-[#1B1815] italic leading-relaxed">{t.about.quote}</blockquote>
                <p className="text-[#17407A] text-[15px] mt-3">— {t.about.name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-6">
                {t.about.specs.map((spec, i) => {
                  const Icon = SPEC_ICONS[i];
                  return (
                    <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }} className="border-t border-[#E6DFD4] pt-4 group">
                      <Icon className="w-5 h-5 text-[#17407A] mb-3 group-hover:scale-110 transition-transform duration-300" />
                      <h3 className="font-serif text-base text-[#1B1815] mb-1">{spec.title}</h3>
                      <p className="text-[#8B817A] text-[15px] leading-relaxed">{spec.desc}</p>
                    </motion.div>
                  );
                })}
              </div>
              <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href={CLINIC.bookingUrl} target="_blank" rel="noreferrer" className="btn-primary mt-8 inline-flex" data-testid="about-cta">{t.about.schedule} <ArrowRight className="w-4 h-4" /></motion.a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== WALK-IN BANNER ===== */}
      {/* Decompression pillar */}
      <section className="py-24 lg:py-32" style={{ background: '#EAE5DD' }} data-testid="home-decompression">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid gap-12 md:grid-cols-2 md:items-center">
          <div>
            <span className="inline-block text-[#1E4E8C] text-[13px] tracking-[0.3em] uppercase font-medium mb-4">{t.pages.decompression.eyebrow}</span>
            <h2 className="font-serif" style={{ fontSize: 'clamp(32px,4.4vw,54px)', lineHeight: 1.06 }}>{t.pages.decompression.introTitle}</h2>
            <p className="mt-6" style={{ fontSize: '18px', lineHeight: 1.7, color: '#3A342E', maxWidth: 520 }}>{t.pages.decompression.introBody}</p>
            <a href={localize('/decompression', lang)} className="btn-primary mt-8 inline-flex" data-testid="home-decompression-link">{t.common.learnMore} <ArrowRight className="w-4 h-4" /></a>
          </div>
          <div className="relative flex aspect-[4/3] items-center justify-center">
            <img src={DRX_HERO} alt="Excite / DRX9000 spinal decompression table with patients" className="h-full w-full scale-[1.1] object-contain" style={{ filter: 'drop-shadow(0 26px 40px rgba(30,45,80,0.22))' }} />
            <span className="absolute bottom-2 left-2 text-[#B4882F] text-[12px] tracking-[0.3em] uppercase">Spinal Decompression</span>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#1B1815]" data-testid="walkin-banner">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <span className="text-white/60 text-[13px] tracking-[0.3em] uppercase font-medium">{t.banner.eyebrow}</span>
              <h2 className="font-serif text-3xl sm:text-4xl text-white mt-3">
                {t.banner.title} <span className="italic text-[#C7D6EA]">{t.banner.titleItalic}</span>
              </h2>
              <p className="text-white/75 text-[18px] mt-4 max-w-xl leading-relaxed">{t.banner.body}</p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 justify-center lg:justify-start">
                {t.banner.bullets.map((item, i) => (
                  <span key={i} className="flex items-center gap-2 text-white/70 text-[16px]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1E4E8C]" />{item}
                  </span>
                ))}
              </div>
            </div>
            <div className="shrink-0 flex flex-col items-center gap-3">
              <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href={CLINIC.phoneHref} className="inline-flex items-center gap-2 bg-[#1E4E8C] text-white px-9 py-5 text-[15px] tracking-[0.12em] uppercase font-semibold hover:bg-[#17407A] transition-colors" data-testid="walkin-banner-call-btn">
                <Phone className="w-5 h-5" /> {t.common.call} — {CLINIC.phone}
              </motion.a>
              <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href="/walk-in" className="inline-flex items-center gap-2 border border-white/50 text-white px-9 py-4 text-[15px] tracking-[0.12em] uppercase font-semibold hover:bg-white/10 transition-colors" data-testid="walkin-banner-btn">
                {t.common.walkin} <ArrowRight className="w-4 h-4" />
              </motion.a>
              <p className="text-white/45 text-[14px] text-center mt-1">{CLINIC.addressLine1} · {CLINIC.addressLine2}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== SERVICES — chapter 01 ===== */}
      <section className="py-24 lg:py-32 bg-[#FFFFFF]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <ChapterHeader n="01" eyebrow={t.servicesSec.eyebrow} title1={t.servicesSec.title1} italic={t.servicesSec.titleItalic} intro={t.servicesSec.intro} />
          <div className="space-y-24 lg:space-y-32 mt-16 lg:mt-24">
            {t.servicesSec.items.map((service, i) => {
              const meta = SVC_META[i]; const Icon = meta.icon; const reversed = i % 2 === 1;
              return (
                <div key={i} className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center" data-testid={`service-card-${i}`}>
                  <motion.div initial={{ opacity: 0, x: reversed ? 50 : -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, ease }} className={reversed ? 'lg:order-2' : ''}>
                    <a href={localize(meta.link, lang)} className="block overflow-hidden group" style={{ clipPath: CLIP }}>
                      <div className="aspect-[4/3] overflow-hidden">
                        <img src={SVC_IMAGES[i]} alt={service.title} loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]" style={{ filter: 'sepia(6%) brightness(1.02) contrast(0.97)' }} />
                      </div>
                    </a>
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }} className={reversed ? 'lg:order-1' : ''}>
                    <div className="flex items-center gap-4 mb-5">
                      <span className="font-serif text-4xl text-[#E6DFD4] leading-none">{String(i + 1).padStart(2, '0')}</span>
                      <span className="h-px w-12" style={{ background: meta.color }} />
                      <Icon className="w-4 h-4" style={{ color: meta.color }} />
                    </div>
                    <h3 className="font-serif text-2xl sm:text-3xl text-[#1B1815] leading-snug">{service.title}</h3>
                    <p className="text-[#8B817A] text-[18px] mt-5 leading-relaxed max-w-md">{service.desc}</p>
                    <div className="mt-7">
                      <span className="text-[12px] tracking-[0.25em] uppercase" style={{ color: meta.color }}>{t.servicesSec.helpsWith}</span>
                      <p className="text-[#3A342E] text-[16px] mt-2 leading-relaxed max-w-md">{service.condition}</p>
                    </div>
                    <a href={localize(meta.link, lang)} className="group inline-flex items-center gap-2 mt-8 text-[13px] tracking-[0.2em] uppercase font-semibold text-[#1B1815] hover:text-[#17407A] transition-colors">
                      {t.common.learnMore} <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                  </motion.div>
                </div>
              );
            })}
          </div>
          <div className="mt-20 lg:mt-28 flex flex-col sm:flex-row items-center justify-between gap-6 pt-10 border-t border-[#E6DFD4]/40">
            <span className="text-[#A79E95] text-[15px]">{t.servicesSec.total}</span>
            <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href="/services" className="btn-outline" data-testid="services-all-btn">{t.common.viewAll} <ArrowRight className="w-4 h-4" /></motion.a>
          </div>
        </div>
      </section>

      {/* ===== TECHNOLOGY — chapter 02, spotlight frames ===== */}
      <section className="py-24 lg:py-32 bg-[#EAE5DD]" data-testid="technology-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <ChapterHeader n="02" eyebrow={t.techSec.eyebrow} title1={t.techSec.title1} italic={t.techSec.titleItalic} intro={t.techSec.intro} />
          <div className="space-y-16 lg:space-y-20 mt-14 lg:mt-20">
            {t.techSec.items.map((tech, i) => {
              const reversed = i % 2 === 1;
              return (
                <div key={i} className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-testid={`tech-card-${i}`}>
                  <motion.div initial={{ opacity: 0, x: reversed ? 50 : -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, ease }} className={reversed ? 'lg:order-2' : ''}>
                    {tech.photo ? (
                      <div className="relative overflow-hidden rounded-2xl group" style={{ boxShadow: '0 24px 44px rgba(30,45,80,0.20)', border: '1px solid rgba(180,136,47,0.35)' }}>
                        <img src={tech.image || TECH_IMAGES[i]} alt={tech.name} loading="lazy" decoding="async" className="aspect-[5/4] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]" />
                      </div>
                    ) : (
                      <div className="relative aspect-[5/4] flex items-center justify-center group">
                        <img src={tech.image || TECH_IMAGES[i]} alt={tech.name} loading="lazy" decoding="async" className="w-full h-full scale-[1.12] object-contain transition-transform duration-[1400ms] ease-out group-hover:scale-[1.18]" style={{ filter: 'drop-shadow(0 24px 30px rgba(30,45,80,0.22))' }} />
                      </div>
                    )}
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }} className={reversed ? 'lg:order-1' : ''}>
                    <div className="flex items-center gap-4 mb-5">
                      <span className="font-serif text-4xl text-[#E6DFD4] leading-none">{String(i + 1).padStart(2, '0')}</span>
                      <span className="h-px w-12 bg-[#17407A]" />
                    </div>
                    <h3 className="font-serif text-2xl sm:text-3xl text-[#1B1815] leading-snug">{tech.name}</h3>
                    <p className="text-[#8B817A] text-[18px] mt-5 leading-relaxed max-w-md">{tech.desc}</p>
                  </motion.div>
                </div>
              );
            })}
          </div>
          <div className="mt-20 lg:mt-28 flex justify-center pt-10 border-t border-[#E6DFD4]/50">
            <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href={localize('/technology', lang)} className="btn-outline" data-testid="technology-btn">{t.common.viewTech} <ArrowRight className="w-4 h-4" /></motion.a>
          </div>
        </div>
      </section>

      {/* ===== PERSONAL INJURY — chapter 03 ===== */}
      <section className="py-24 lg:py-32 bg-[#FFFFFF]" data-testid="pi-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, ease }}>
              <div className="aspect-[4/3] overflow-hidden" style={{ clipPath: CLIP }}>
                <img src={PI_IMG} alt="Auto accident injury care" className="w-full h-full object-cover" style={{ filter: 'sepia(6%) brightness(1.02) contrast(0.97)' }} />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }}>
              <span className="text-[#1E4E8C] text-[13px] tracking-[0.3em] uppercase font-semibold">{t.pi.eyebrow}</span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1B1815] mt-5 leading-[1.08]">
                {t.pi.title1} <span className="italic text-[#1E4E8C]">{t.pi.titleItalic}</span>
              </h2>
              <p className="text-[#3A342E] text-[18px] mt-6 leading-relaxed max-w-lg">{t.pi.body}</p>
              <ul className="mt-7 space-y-3">
                {t.pi.bullets.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[#1B1815] text-[17px]">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1E4E8C] shrink-0" />{item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 mt-9">
                <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href={CLINIC.phoneHref} className="btn-outline" data-testid="pi-call-btn" style={{ borderColor: '#1E4E8C', color: '#1E4E8C' }}><Phone className="w-4 h-4" /> {t.common.call} — {CLINIC.phone}</motion.a>
                <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href={localize('/personal-injury', lang)} className="btn-primary" data-testid="pi-walkin-btn">{t.common.getSeen} <ArrowRight className="w-4 h-4" /></motion.a>
              </div>
              <p className="text-[#1E4E8C] text-[15px] mt-6 italic">{t.pi.attorneys}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CONVENIENT PAYMENT OPTIONS ===== */}
      <PaymentOptions />

      {/* ===== CTA ===== */}
      <section className="py-24 lg:py-32 relative overflow-hidden" style={{ background: '#EAE5DD' }} data-testid="cta-section">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E6DFD4]/60 to-transparent" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, ease }}>
            <span className="text-[#1E4E8C] text-[13px] tracking-[0.35em] uppercase font-medium">{t.cta.eyebrow}</span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1B1815] mt-4 mb-6">
              {t.cta.title1} <span className="italic text-[#17407A]">{t.cta.titleItalic}</span>
            </h2>
            <p className="text-[#3A342E] mb-10 max-w-xl mx-auto text-[18px] leading-relaxed">{t.cta.body}</p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center">
              <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href={CLINIC.bookingUrl} target="_blank" rel="noreferrer" className="btn-primary" data-testid="cta-book-btn">{t.common.book} <ArrowRight className="w-4 h-4" /></motion.a>
              <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href={CLINIC.phoneHref} className="btn-outline" data-testid="cta-call-btn" style={{ borderColor: '#1B1815', color: '#1B1815' }}><Phone className="w-4 h-4" /> {t.common.call}</motion.a>
              <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href={localize('/walk-in', lang)} className="btn-outline" data-testid="cta-walkin-btn" style={{ borderColor: '#1E4E8C', color: '#1E4E8C' }}>⚡ {t.common.walkin}</motion.a>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

const ChapterHeader = ({ n, eyebrow, title1, italic, intro }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="max-w-2xl">
    <div className="flex items-center gap-4 mb-5">
      <span className="font-serif text-2xl text-[#1E4E8C]">{n}</span>
      <span className="h-px w-16 bg-[#1E4E8C]/40" />
      <span className="text-[#17407A] text-[13px] tracking-[0.3em] uppercase font-medium">{eyebrow}</span>
    </div>
    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1B1815] leading-[1.08]">
      {title1} <span className="italic text-[#1E4E8C]">{italic}</span>
    </h2>
    <p className="text-[#8B817A] text-[18px] mt-6 leading-relaxed">{intro}</p>
  </motion.div>
);

export default HomePage;
;
